import type { Cart } from '../types/carts';
import { IProduct, ProductCategory } from '../types/Product';

/** mirrors web's HARDWARE_FIRST_MAX_UNITS_PER_CART - the server rejects carts above it */
export const HARDWARE_FIRST_MAX_UNITS_PER_CART = 5;

/**
 * Whole percent off a repriced tracker, rounded down. The price set stores dollar prices rather
 * than rates, so the effective discount drifts either side of the intended one: series 3+ at $83
 * off $129 is 35.66%, which rounds up to an advertised 36% on a 35% tier. Rounding down keeps the
 * claim at or below what the shopper is actually charged.
 */
export function hardwareDiscountPercentOff(listPriceInCents: number, discountedPriceInCents: number): number {
  if (listPriceInCents <= 0) {
    return 0;
  }

  return Math.max(Math.floor((1 - discountedPriceInCents / listPriceInCents) * 100), 0);
}

/**
 * Total hardware-first units in the cart: standalone (subscription-less) collar items. Only
 * meaningful for enrolled visitors - callers gate on enrollment; the server remains the
 * authority on which collars are actually repriced.
 *
 * Kept out of `lib/cart.ts` for the same reason as `lib/freeShipping.ts`: that module reaches
 * `lib/product` → `lib/util` → `uuid`, which Jest cannot transform; as a leaf this stays
 * unit-testable without mocking that chain.
 */
export function hardwareFirstUnitCount(cart: Cart, products: IProduct[]): number {
  const collarSkus = new Set(
    products.flatMap((product) => {
      if (product.category === ProductCategory.COLLAR_KIT) {
        return product.variants.map((variant) => variant.sku);
      }
      if (product.category === ProductCategory.MINI || product.category === ProductCategory.ULTRA) {
        return [product.sku];
      }
      return [];
    }),
  );

  return Object.values(cart.cartItems).reduce((count, cartItem) => {
    const isStandaloneItem = !('subscriptionLineItem' in cartItem) && !('giftCardDelivery' in cartItem);
    return isStandaloneItem && collarSkus.has(cartItem.lineItem.sku) ? count + cartItem.quantity : count;
  }, 0);
}
